import { useEffect, useState, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Upload, X, FileText, FileSpreadsheet, Presentation, Image } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { useCreateProject, useUpdateProject } from '@/hooks/useProjects';
import { useUploadProjectFile } from '@/hooks/useProjectFiles';
import { toast } from 'sonner';
import {
  ACCEPT_ALL,
  MAX_PROJECT_FILES,
  isSupportedExtension,
  formatFileSize,
  validateUploadSize,
} from '@/lib/fileConstants';
import type { Tables } from '@/integrations/supabase/types';

type ScopeType = 'global' | 'personal';

const projectSchema = z.object({
  name: z.string().trim().min(2, 'Nome deve ter pelo menos 2 caracteres').max(100),
  icon: z.string().trim().min(1).max(10).default('📁'),
  description: z.string().trim().max(500).optional().or(z.literal('')),
  scopeValue: z.enum(['global', 'personal']),
  instructions: z.string().trim().max(5000).optional().or(z.literal('')),
});

type ProjectFormValues = z.infer<typeof projectSchema>;

function getFileIcon(fileType: string) {
  const ext = fileType.split('.').pop()?.toLowerCase() || fileType;
  if (['xlsx', 'xls', 'csv'].includes(ext)) return <FileSpreadsheet className="w-4 h-4 text-green-500 shrink-0" />;
  if (['pptx', 'ppt'].includes(ext)) return <Presentation className="w-4 h-4 text-orange-500 shrink-0" />;
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return <Image className="w-4 h-4 text-purple-500 shrink-0" />;
  return <FileText className="w-4 h-4 text-blue-500 shrink-0" />;
}

interface ProjectFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project?: Tables<'projects'>;
  defaultScope?: ScopeType;
}

export const ProjectFormModal = ({
  open,
  onOpenChange,
  project,
  defaultScope,
}: ProjectFormModalProps) => {
  const { user } = useAuth();
  const { isAdmin } = useProfile();
  const createProject = useCreateProject();
  const updateProject = useUpdateProject();
  const uploadFile = useUploadProjectFile();
  const isEditing = !!project;

  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<ProjectFormValues>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      name: '',
      icon: '📁',
      description: '',
      scopeValue: (defaultScope === 'global' && isAdmin ? 'global' : 'personal'),
      instructions: '',
    },
  });

  useEffect(() => {
    if (open) {
      setStagedFiles([]);
      if (project) {
        form.reset({
          name: project.name,
          icon: project.icon,
          description: project.description ?? '',
          scopeValue: (project.scope_type as ScopeType) === 'global' ? 'global' : 'personal',
          instructions: project.instructions ?? '',
        });
      } else {
        const effectiveScope: ScopeType = (defaultScope === 'global' && isAdmin) ? 'global' : 'personal';
        form.reset({
          name: '',
          icon: '📁',
          description: '',
          scopeValue: effectiveScope,
          instructions: '',
        });
      }
    }
  }, [open, project, defaultScope, isAdmin]);

  const addFiles = useCallback((newFiles: File[]) => {
    const validFiles: File[] = [];
    for (const file of newFiles) {
      if (!isSupportedExtension(file.name)) {
        toast.error(`Tipo não suportado: .${file.name.split('.').pop()}`);
        continue;
      }
      const sizeError = validateUploadSize(file);
      if (sizeError) {
        toast.error(`"${file.name}": ${sizeError}`);
        continue;
      }
      validFiles.push(file);
    }
    setStagedFiles(prev => {
      const combined = [...prev, ...validFiles];
      if (combined.length > MAX_PROJECT_FILES) {
        toast.error(`Máximo de ${MAX_PROJECT_FILES} arquivos por projeto`);
        return combined.slice(0, MAX_PROJECT_FILES);
      }
      return combined;
    });
  }, []);

  const removeFile = useCallback((index: number) => {
    setStagedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const onSubmit = async (values: ProjectFormValues) => {
    try {
      if (isEditing) {
        await updateProject.mutateAsync({
          id: project.id,
          name: values.name,
          icon: values.icon,
          description: values.description || null,
          scope_type: values.scopeValue,
          scope_id: null,
          instructions: values.instructions || null,
        });
        toast.success('Projeto atualizado');
      } else {
        const created = await createProject.mutateAsync({
          name: values.name,
          icon: values.icon,
          description: values.description || null,
          scope_type: values.scopeValue,
          scope_id: null,
          owner_id: user!.id,
          instructions: values.instructions || null,
        });

        // Upload staged files after project creation
        if (stagedFiles.length > 0 && created?.id) {
          const filesToUpload = [...stagedFiles];
          // Close modal first, then upload in background
          onOpenChange(false);
          toast.info(`Enviando ${filesToUpload.length} arquivo(s)...`);
          const results = await Promise.allSettled(
            filesToUpload.map(file =>
              uploadFile.mutateAsync({ projectId: created.id, file })
            )
          );
          const failed = results.filter(r => r.status === 'rejected');
          if (failed.length > 0) {
            failed.forEach(r => {
              if (r.status === 'rejected') {
                console.error('Upload failed:', r.reason);
                toast.error(`Erro no upload: ${(r.reason as Error).message}`);
              }
            });
            toast.success(`Projeto criado. ${results.length - failed.length}/${filesToUpload.length} arquivo(s) enviado(s).`);
          } else {
            toast.success(`Projeto criado com ${filesToUpload.length} arquivo(s)`);
          }
          return; // already closed modal above
        } else {
          toast.success('Projeto criado com sucesso');
        }
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(`Erro ao salvar projeto: ${(err as Error).message}`);
    }
  };

  const isPending = createProject.isPending || updateProject.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[540px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar Projeto' : 'Novo Projeto'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="flex gap-3">
            <div className="w-16">
              <label className="block text-sm font-medium text-primary mb-1.5">Ícone</label>
              <input
                {...form.register('icon')}
                className="w-full px-2 py-2 rounded-md border border-color bg-surface text-center text-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-primary mb-1.5">Nome *</label>
              <input
                {...form.register('name')}
                className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
                placeholder="Nome do projeto"
              />
              {form.formState.errors.name && (
                <p className="text-xs text-destructive mt-1">{form.formState.errors.name.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1.5">Descrição</label>
            <textarea
              {...form.register('description')}
              rows={2}
              className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary resize-none"
              placeholder="Descrição opcional"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1.5">Ambiente *</label>
            <select
              {...form.register('scopeValue')}
              className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
            >
              {isAdmin && <option value="global">🏢 Espaço Caldeira</option>}
              <option value="personal">👤 Pessoal</option>
            </select>
            {!isEditing && (
              <p className="text-xs text-tertiary mt-1">
                {form.watch('scopeValue') === 'global'
                  ? 'O projeto será privado por padrão. Você poderá convidar pessoas e times depois.'
                  : 'Apenas você terá acesso a este projeto.'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1.5">Instruções do projeto</label>
            <textarea
              {...form.register('instructions')}
              rows={3}
              className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary resize-none"
              placeholder="Instruções persistentes para o assistente neste projeto..."
            />
          </div>

          {/* File upload section — only for new projects */}
          {!isEditing && (
            <div>
              <label className="block text-sm font-medium text-primary mb-1.5">
                Arquivos <span className="text-tertiary font-normal">(opcional)</span>
              </label>
              <p className="text-xs text-tertiary mb-2">
                Estes arquivos serão anexados como base de conhecimento do projeto.
              </p>

              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  addFiles(Array.from(e.dataTransfer.files));
                }}
                onClick={() => fileInputRef.current?.click()}
                className={`flex flex-col items-center justify-center gap-1.5 px-4 py-5 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                  isDragging ? 'border-brand-primary bg-hover' : 'border-color hover:bg-hover'
                }`}
              >
                <Upload className={`w-6 h-6 ${isDragging ? 'text-brand-primary' : 'text-secondary'}`} />
                <span className="text-xs text-secondary">Arraste arquivos ou clique para selecionar</span>
                <span className="text-[10px] text-tertiary">PDF, DOCX, PPTX, XLSX, TXT, MD, CSV, PNG, JPG — máx. 128 MB cada</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT_ALL}
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    addFiles(Array.from(e.target.files ?? []));
                    e.target.value = '';
                  }}
                />
              </div>

              {stagedFiles.length > 0 && (
                <div className="mt-2 space-y-1">
                  {stagedFiles.map((file, i) => (
                    <div key={`${file.name}-${i}`} className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-secondary">
                      {getFileIcon(file.name)}
                      <span className="flex-1 text-sm text-primary truncate">{file.name}</span>
                      <span className="text-xs text-tertiary shrink-0">{formatFileSize(file.size)}</span>
                      <button
                        type="button"
                        onClick={() => removeFile(i)}
                        className="p-0.5 rounded hover:bg-active"
                      >
                        <X className="w-3.5 h-3.5 text-secondary" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 border border-color rounded-md text-sm font-medium text-primary hover:bg-hover"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-4 py-2 brand-primary rounded-md text-sm font-medium disabled:opacity-50"
            >
              {isPending ? 'Salvando...' : isEditing ? 'Salvar' : 'Criar Projeto'}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};